import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import L from 'leaflet';
import { Box } from '@chakra-ui/react';
import geohash from 'ngeohash';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

interface MapBackgroundRef {
  clearMarkers: () => void;
}

const MapBackground = forwardRef<MapBackgroundRef>((props, ref) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const popups = useRef<L.Popup[]>([]);

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = L.map(mapContainer.current, {
      zoomControl: false,
      dragging: true,
      touchZoom: true,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
    }).setView([37.7749, -122.4194], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
    }).addTo(map.current);

    // Track current popup
    let currentPopup: L.Popup | null = null;
    
    // Add click handler to show popups
    map.current.on('click', (e: L.LeafletMouseEvent) => {
      // If popup exists, just close it
      if (currentPopup) {
        map.current!.removeLayer(currentPopup);
        const index = popups.current.indexOf(currentPopup);
        if (index > -1) {
          popups.current.splice(index, 1);
        }
        currentPopup = null;
        return; // Don't open a new popup
      }
      
      // No popup open, create a new one
      const lat = e.latlng.lat;
      const lng = e.latlng.lng;
      const geoHash = geohash.encode(lat, lng, 9);
      
      const popupContent = `
        <div style="min-width: 200px;">
          <strong>Geohash:</strong> ${geoHash}<br/>
          <strong>Latitude:</strong> ${lat.toFixed(6)}<br/>
          <strong>Longitude:</strong> ${lng.toFixed(6)}
        </div>
      `;
      
      const popup = L.popup()
        .setLatLng([lat, lng])
        .setContent(popupContent)
        .openOn(map.current!);
      
      currentPopup = popup;
      popups.current.push(popup);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    clearMarkers: () => {
      popups.current.forEach(popup => {
        if (map.current) {
          map.current.removeLayer(popup);
        }
      });
      popups.current = [];
    }
  }));

  return (
    <Box
      ref={mapContainer}
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={0}
    />
  );
});

MapBackground.displayName = 'MapBackground';

export default MapBackground;